import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import {
  Users,
  ShoppingCart,
  Clock,
  Plus,
  FileText,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity,
  CheckCircle,
  AlertCircle,
  Info,
  ArrowRight,
  Loader2,
  UserPlus
} from 'lucide-react';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectDashboardHref, selectCurrentClinic, selectClinicsLoading } from '@/store/slices/authSlice';
import { analyticsApi, type DashboardResponse } from '@/services/analyticsService';
import { useQuickActions } from '@/contexts/QuickActionsContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const dashboardHref = useSelector(selectDashboardHref);
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openCreatePatientModal, openCreateUserModal } = useQuickActions();

  useEffect(() => {
    const fetchDashboard = async () => {
      // Wait for clinics to load first
      if (clinicsLoading) {
        return;
      }

      if (!currentClinic) {
        setError('No clinic selected');
        setLoading(false);
        return;
      }

      // Hardcoded dashboard data for UI demo
      const mockDashboardData: DashboardResponse = {
        statistics: {
          total_orders: {
            value: 324,
            change: 12.5,
            change_period: "vs. last 30 days",
            change_direction: "up",
            unit: ""
          },
          active_orders: {
            value: 47,
            change: 8.3,
            change_period: "vs. last 7 days",
            change_direction: "up",
            unit: ""
          },
          average_fulfillment_time: {
            value: 2.4,
            change: 15.2,
            change_period: "vs. last 30 days",
            change_direction: "down",
            unit: "days"
          },
          total_patients: {
            value: 1248,
            change: 5.7,
            change_period: "vs. last 30 days",
            change_direction: "up",
            unit: ""
          }
        },
        recent_activity: [
          {
            title: "New prescription created",
            subtitle: "Order #RX-2024-3421 for patient John Smith",
            badge: "Prescription",
            status: "success",
            time: new Date(Date.now() - 15 * 60000).toISOString() // 15 mins ago
          },
          {
            title: "Patient registered",
            subtitle: "Mary Johnson added to clinic database",
            badge: "Patient",
            status: "success",
            time: new Date(Date.now() - 45 * 60000).toISOString() // 45 mins ago
          },
          {
            title: "Order pending approval",
            subtitle: "Order #RX-2024-3420 requires prescriber review",
            badge: "Pending",
            status: "warning",
            time: new Date(Date.now() - 2 * 3600000).toISOString() // 2 hours ago
          },
          {
            title: "Refill requested",
            subtitle: "Patient Robert Davis requested medication refill",
            badge: "Refill",
            status: "info",
            time: new Date(Date.now() - 3 * 3600000).toISOString() // 3 hours ago
          },
          {
            title: "Order completed",
            subtitle: "Order #RX-2024-3419 delivered to patient",
            badge: "Completed",
            status: "success",
            time: new Date(Date.now() - 5 * 3600000).toISOString() // 5 hours ago
          }
        ],
        links: {
          self: { href: "/clinic/123/dashboard" },
          orders: { href: "/clinic/123/orders" },
          patients: { href: "/clinic/123/patients" }
        }
      };

      setDashboardData(mockDashboardData);
      setLoading(false);
      setError(null);

      // Original API call commented out for when endpoint is ready
      // if (!dashboardHref) {
      //   setError('Dashboard not available for this clinic');
      //   setLoading(false);
      //   return;
      // }

      // try {
      //   setLoading(true);
      //   setError(null);
      //   const data = await analyticsApi.getDashboard(dashboardHref);
      //   setDashboardData(data);
      // } catch (err) {
      //   setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      // } finally {
      //   setLoading(false);
      // }
    };

    fetchDashboard();
  }, [dashboardHref, currentClinic, clinicsLoading]);

  const handleCreateOrder = () => {
    navigate('/create-order');
  };

  // Transform API data to component format
  const getStats = () => {
    if (!dashboardData) return [];

    const { statistics } = dashboardData;
    return [
      {
        title: "Total Orders",
        value: statistics.total_orders.value.toString(),
        change: `+${statistics.total_orders.change}%`,
        changeLabel: statistics.total_orders.change_period,
        icon: ShoppingCart,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-l-emerald-500",
        trend: statistics.total_orders.change_direction
      },
      {
        title: "Active Orders",
        value: statistics.active_orders.value.toString(),
        change: `${statistics.active_orders.change_direction === 'up' ? '+' : '-'}${statistics.active_orders.change}%`,
        changeLabel: statistics.active_orders.change_period,
        icon: Activity,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-l-emerald-500",
        trend: statistics.active_orders.change_direction
      },
      {
        title: "Avg Fulfillment Time",
        value: `${statistics.average_fulfillment_time.value}${statistics.average_fulfillment_time.unit[0]}`,
        change: `${statistics.average_fulfillment_time.change}% faster`,
        changeLabel: statistics.average_fulfillment_time.change_period,
        icon: Clock,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-l-emerald-500",
        // Invert the trend for fulfillment time - down is good!
        trend: statistics.average_fulfillment_time.change_direction === 'down' ? 'up' : 'down'
      },
      {
        title: "Total Patients",
        value: statistics.total_patients.value.toString(),
        change: `+${statistics.total_patients.change}%`,
        changeLabel: statistics.total_patients.change_period,
        icon: Users,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-l-emerald-500",
        trend: statistics.total_patients.change_direction
      }
    ];
  };

  // Transform API activity data
  const getRecentActivity = () => {
    if (!dashboardData) return [];

    return dashboardData.recent_activity.map(activity => ({
      ...activity,
      time: new Date(activity.time).toRelativeTime(),
      icon: activity.status === 'success' ? CheckCircle : 
            activity.title.includes('Patient') ? Users :
            activity.title.includes('pending') ? AlertCircle : Info
    }));
  };

  // Quick actions matching the sidebar
  const getQuickActions = () => {
    return [
      {
        title: "Create New Patient",
        description: "Add a patient to the system",
        icon: Plus,
        primary: true,
        action: openCreatePatientModal
      },
      {
        title: "Create New User",
        description: "Add a new clinic user",
        icon: UserPlus,
        primary: false,
        action: openCreateUserModal
      }
    ];
  };

  if (loading || clinicsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 font-lato">
        <ResponsiveNavigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            <p className="text-vitl-dark/70 font-lato">
              {clinicsLoading ? 'Loading clinic data...' : 'Loading dashboard...'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 font-lato">
        <ResponsiveNavigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-red-600">
            <p>Error loading dashboard: {error}</p>
          </div>
        </main>
      </div>
    );
  }

  const stats = getStats();
  const recentActivity = getRecentActivity();
  const quickActions = getQuickActions();

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-8 sm:px-6 lg:pl-24 lg:pr-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 pb-6 border-b border-gray-200">
          <h1 className="text-2xl md:text-3xl font-syne font-light text-vitl-navy mb-1">
            Welcome back, <span className="font-medium">Dr. Johnson</span>
          </h1>
          <p className="text-sm md:text-base text-vitl-dark/60 font-lato">
            Here's what's happening at <span className="font-bold">{currentClinic?.name || 'your clinic'}</span>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index} className={`border-l-4 ${stat.borderColor} shadow-sm hover:shadow-md transition-all duration-200`}>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <div className={`p-2 md:p-3 rounded-xl ${stat.bgColor} ${stat.color}`}>
                    <stat.icon className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className={`flex items-center ${stat.trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {stat.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                </div>
                <div className="space-y-1 md:space-y-2">
                  <p className="text-xs md:text-sm font-lato font-medium text-vitl-dark/70">{stat.title}</p>
                  <p className="text-2xl md:text-3xl font-lato font-bold text-vitl-navy">{stat.value}</p>
                  <div className="flex items-center space-x-1">
                    <span className={`text-xs md:text-sm font-medium ${stat.trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {stat.change}
                    </span>
                    <span className="text-xs font-lato text-vitl-dark/50">{stat.changeLabel}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <Card className="lg:col-span-1 border-l-4 border-l-[#1b4736] shadow-sm hover:shadow-md transition-all duration-200">
            <CardHeader>
              <CardTitle className="text-lg font-syne font-medium text-vitl-navy flex items-center">
                <Plus className="w-5 h-5 mr-2 text-[#1b4736]" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-md font-medium transition-colors duration-200 text-sm border-l-4 ${
                    action.primary
                      ? 'bg-white hover:bg-emerald-50 text-emerald-600 border-l-emerald-600 border border-emerald-200'
                      : 'bg-white hover:bg-emerald-50 text-emerald-600 border-l-emerald-600 border border-emerald-200'
                  }`}
                >
                  <action.icon className="w-4 h-4" />
                  <span>{action.title}</span>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2 border-l-4 border-l-[#1b4736] shadow-sm hover:shadow-md transition-all duration-200">
            <CardHeader>
              <CardTitle className="text-lg font-syne font-medium text-vitl-navy flex items-center">
                <Activity className="w-5 h-5 mr-2 text-[#1b4736]" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200 border border-transparent hover:border-gray-200">
                    <div className={`p-2 rounded-lg ${
                      activity.status === 'success' ? 'bg-emerald-50 text-emerald-600' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      <activity.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <p className="font-lato font-medium text-vitl-navy">{activity.title}</p>
                        <Badge className={`text-xs font-lato ${
                          activity.status === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {activity.badge}
                        </Badge>
                      </div>
                      <p className="text-sm text-vitl-dark/70 font-lato">{activity.subtitle}</p>
                    </div>
                    <div className="text-xs text-vitl-dark/50 font-lato whitespace-nowrap">{activity.time}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Link to="/orders" className="text-sm font-lato font-medium text-emerald-600 hover:text-emerald-700 flex items-center group">
                  View all activity
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

    </div>
  );
};

// Add helper to format relative time
declare global {
  interface Date {
    toRelativeTime(): string;
  }
}

Date.prototype.toRelativeTime = function() {
  const now = new Date();
  const diffMs = now.getTime() - this.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return this.toLocaleDateString();
};

export default Dashboard;